import { darken, rgba } from 'polished';
import { muiTheme } from './muiTheme';

export const phi = 1.61803398875;

const colors = {
  dark: {
    primaryColor: '#31364b', // #17161b // #f0f1f6
    secondaryColor: '#f0f1f6', // #888daa

    tertiaryColorLight: '#33bfd4',
    tertiaryColor: '#00b0ca', // #00146b
    tertiaryColorDark: '#007b8d',
    accentColorLight: '#febf33',
    accentColor: '#feb000', // #f1a501
    accentColorDark: '#b17b00',
    whiteColor: '#ffffff',
    blackColor: '#000000',
    grayColorLight: '#ffffff14',
    grayColor: '#bbbbbb',
    grayDarkColor: '#31364b',
    redColorLight: '#ef5350',
    redColor: '#d32f2f',
    redColorDark: '#c62828',
    blueColorLight: '#03a9f4',
    blueColor: '#0288d1',
    blueColorDark: '#01579b',
    orangeColorLight: '#ff9800',
    orangeColor: '#ed6c02',
    orangeColorDark: '#e65100',
    greenColorLight: '#4caf50',
    greenColor: '#2e7d32',
    greenColorDark: '#1b5e20',
  },
  light: {
    primaryColor: '#f4f6fb', // #f4f6fb // #f0f1f6
    secondaryColor: '#31364b', // #242730

    tertiaryColorLight: '#33bfd4',
    tertiaryColor: '#00b0ca', // #00146b
    tertiaryColorDark: '#007b8d',
    accentColorLight: '#febf33',
    accentColor: '#feb000', // #f1a501
    accentColorDark: '#b17b00',
    whiteColor: '#ffffff',
    blackColor: '#000000',
    grayColorLight: '#0000000a',
    grayColor: '#bbbbbb',
    grayDarkColor: '#31364b',
    redColorLight: '#ef5350',
    redColor: '#d32f2f',
    redColorDark: '#c62828',
    blueColorLight: '#03a9f4',
    blueColor: '#0288d1',
    blueColorDark: '#01579b',
    orangeColorLight: '#ff9800',
    orangeColor: '#ed6c02',
    orangeColorDark: '#e65100',
    greenColorLight: '#4caf50',
    greenColor: '#2e7d32',
    greenColorDark: '#1b5e20',
  },
};

/**
 * theme.js
 * https://github.com/system-ui/theme-specification
 */

// emotion breakpoints
// const breakpoints = ['40em', '52em', '64em', '80em', '120em'];
// custom bootstrap 4 breakpoints
const breakpoints = ['576px', '768px', '992px', '1200px', '1800px'];
// aliases
breakpoints.xs = breakpoints[0];
breakpoints.sm = breakpoints[1];
breakpoints.md = breakpoints[2];
breakpoints.lg = breakpoints[3];
breakpoints.xl = breakpoints[4];

// export const breakpoints = {
//   xs: 300,
//   sm: 576,
//   md: 768,
//   lg: 992,
//   xl: 1200,
//   xxl: 1400,
// };

// export const mediaQueries = key => style =>
//   `@media (min-width: ${key}px) { ${style} }`;

const styledSystemTheme = mode => ({
  mode,
  colors: colors[mode],
  mediaQueries: {
    sm: `@media screen and (min-width: ${breakpoints[1]})`,
    md: `@media screen and (min-width: ${breakpoints[2]})`,
    lg: `@media screen and (min-width: ${breakpoints[3]})`,
    xl: `@media screen and (min-width: ${breakpoints[4]})`,
  },
  breakpoints,
  fontSizes: [
    '0.8rem', // 0
    '0.9rem', // 1
    '1rem', // 2
    '1.1rem', // 3
    '1.2rem', // 4
    '1.3rem', // 5
    '1.4rem', // 6
    '1.5rem', // 7
    '1.6rem', // 8
    '1.7rem', // 0
    '1.8rem', // 10
    '1.9rem', // 11
    '2rem', // 12
    '2.1rem', // 13
    '2.2rem', // 14
    '2.3rem', // 15
    '2.4rem', // 16
    '2.5rem', // 17
    '3rem', // 18
    '3.5rem', // 19
    '4rem', // 20
    '4.5rem', // 21
    '5rem', // 22
    '5.5rem', // 23
  ],
  fontWeights: [
    'normal', // 0
    'bold', // 1
    100, // 2
    200, // 3
    300, // 4
    400, // 5
    500, // 6
    600, // 7
    700, // 8
    800, // 9
    900, // 10
  ],
  lineHeights: [1, 2],
  radii: {
    xs: '4px',
    sm: '6px',
    md: '8px',
    lg: '10px',
    xl: '12px',
    high: '1000px',
  },
  space: [
    0, // 0
    4, // 1
    8, // 2
    12, // 3
    16, // 4
    24, // 5
    32, // 6
    48, // 7
    64, // 8
    96, // 9
    128, // 10
    192, // 11
    256, // 12
  ],
  sizes: [
    0, // 0
    4, // 1
    8, // 2
    12, // 3
    16, // 4
    24, // 5
    32, // 6
    48, // 7
    64, // 8
    96, // 9
    128, // 10
    192, // 11
    256, // 12
  ],
  zIndices: [
    -1, // 0
    0, // 1
    1, // 2
    2, // 3
    3, // 4
    4, // 5
    5, // 6
    100, // 7
    101, // 8
    102, // 9
    103, // 10
    104, // 11
    105, // 12
    1000, // 13
    1001, // 14
    1002, // 15
    1003, // 16
    1004, // 17
    1005, // 18
    2000, // 19
  ],
  outline: [
    'none', // 0
    '#aaa solid 1px;', // 1
  ],
  // shadows: {
  //   card: ` 0 ${phi / 20}rem ${phi / 12}rem ${rgba(
  //     colors[mode].blackColor,
  //     0.15,
  //   )}`,
  //   input: '0 0 4px rgba(0, 0, 0, 0.05)',
  // },
  borders: [
    `1px solid ${rgba(colors[mode].secondaryColor, 0.3)}`, // 0
    `1px solid ${darken(0.1, colors[mode].primaryColor)}`,
  ],
  borderWidths: [0, 1, 2, 3],
  variants: {
    card: {
      p: 2,
      bg: 'background',
      boxShadow: 25,
      borderRadius: 2,
    },
    badge: {
      display: 'inline-block',
      p: 1,
      color: 'white',
      bg: 'green',
      borderRadius: 2,
    },
  },
});

const getTheme = mode => ({
  ...styledSystemTheme(mode),
  ...muiTheme(styledSystemTheme(mode)),
});

export default getTheme;
